import { useCallback, useEffect, useState } from "react";
import EmptyState from "../components/EmptyState";
import ErrorAlert from "../components/ErrorAlert";
import FieldError from "../components/FieldError";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import { adminApi } from "../services/adminApi";
import { getResults } from "../utils/formatters";

const emptyCategory = {
  name: "",
  description: "",
  display_order: 0,
  is_active: true,
};

function CategoryForm({ category, fieldErrors, saving, onCancel, onChange, onSubmit }) {
  return (
    <form className="card admin-panel" onSubmit={onSubmit}>
      <div className="card-body flex flex-column gap-4">
        <label className="form-group">
          <span className="form-label">Name *</span>
          <input
            aria-invalid={Boolean(fieldErrors.name)}
            value={category.name}
            onChange={(event) => onChange({ name: event.target.value })}
          />
          <FieldError message={fieldErrors.name} />
        </label>
        <label className="form-group">
          <span className="form-label">Description</span>
          <textarea value={category.description || ""} onChange={(event) => onChange({ description: event.target.value })} />
          <FieldError message={fieldErrors.description} />
        </label>
        <label className="form-group">
          <span className="form-label">Display order</span>
          <input type="number" min="0" value={category.display_order} onChange={(event) => onChange({ display_order: event.target.value })} />
          <FieldError message={fieldErrors.display_order} />
        </label>
        <label className="checkbox-group">
          <input type="checkbox" checked={Boolean(category.is_active)} onChange={(event) => onChange({ is_active: event.target.checked })} />
          Active
        </label>
        <div className="flex gap-3 flex-wrap">
          <button className="btn btn-primary" disabled={saving || !category.name} type="submit">
            {saving ? "Saving" : "Save Category"}
          </button>
          <button className="btn btn-secondary" disabled={saving} onClick={onCancel} type="button">
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  const loadCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await adminApi("/categories/");
      setCategories(getResults(payload));
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  function updateEditingCategory(changes) {
    setEditingCategory((currentCategory) => ({ ...currentCategory, ...changes }));
  }

  async function saveCategory(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setSuccess("");

    const isNew = !editingCategory.id;

    try {
      await adminApi(isNew ? "/categories/" : `/categories/${editingCategory.id}/`, {
        method: isNew ? "POST" : "PATCH",
        body: JSON.stringify(editingCategory),
      });
      setEditingCategory(null);
      setSuccess("Category saved.");
      await loadCategories();
    } catch (requestError) {
      setError(requestError);
      setFieldErrors(requestError.fieldErrors || {});
    } finally {
      setSaving(false);
    }
  }

  async function deactivateOrDeleteCategory(category) {
    setError(null);
    setSuccess("");

    try {
      const response = await adminApi(`/categories/${category.id}/`, { method: "DELETE" });
      setSuccess(response?.detail || "Category removed.");
      await loadCategories();
    } catch (requestError) {
      setError(requestError);
    }
  }

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Menu" title="Category Management">
        Create and organize categories used by menu items.
      </PageTitle>

      {error ? <ErrorAlert error={error} onRetry={loadCategories} /> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {editingCategory ? (
        <CategoryForm
          category={editingCategory}
          fieldErrors={fieldErrors}
          saving={saving}
          onCancel={() => setEditingCategory(null)}
          onChange={updateEditingCategory}
          onSubmit={saveCategory}
        />
      ) : (
        <button className="btn btn-primary" onClick={() => setEditingCategory(emptyCategory)} type="button">
          New Category
        </button>
      )}

      <section className="card admin-panel">
        <header className="card-header">
          <h2>Categories</h2>
        </header>
        <div className="card-body">
          {loading ? <LoadingState /> : null}
          {!loading && !error && !categories.length ? (
            <EmptyState
              text="No categories yet. Create your first category to begin adding food."
              action={<button className="btn btn-primary" onClick={() => setEditingCategory(emptyCategory)} type="button">Create Category</button>}
            />
          ) : null}
          {!loading && categories.length ? (
            <div className="admin-list">
              {categories.map((category) => (
                <article key={category.id}>
                  <strong>{category.name}</strong>
                  <span>{category.description || "No description"}</span>
                  <span>Order {category.display_order} - {category.is_active ? "Active" : "Inactive"}</span>
                  <div className="flex gap-2 flex-wrap">
                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingCategory(category)} type="button">
                      Edit
                    </button>
                    <button className="btn btn-sm btn-light" onClick={() => deactivateOrDeleteCategory(category)} type="button">
                      Delete / Deactivate
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default CategoriesPage;